import React, { useEffect, useState } from 'react';
import { apiRequestBlob } from '@/lib/api/client';
import { doctoralApi } from '@/features/workspace/api';
import { DoctoralTopicData } from '../types';
import { DoctoralSectionModes } from './DoctoralSectionModes';
import { Card, DocRow, Field, Hero } from './dp120Parts';

interface DoctoralTopicViewProps {
  data: DoctoralTopicData | null;
  onOpenAdvisorModal: () => void;
}

type TopicTab = 'prepare' | 'supervisor' | 'foreign' | 'approval';

export const DoctoralTopicView: React.FC<DoctoralTopicViewProps> = ({ data, onOpenAdvisorModal }) => {
  const [tab, setTab] = useState<TopicTab>('prepare');
  const [form, setForm] = useState<DoctoralTopicData>(data || {});
  const [notice, setNotice] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setForm(data || {});
  }, [data]);

  const set = (key: keyof DoctoralTopicData) => (value: string) => {
    setDirty(true);
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  const text = (key: keyof DoctoralTopicData) => String(form[key] ?? '');

  const save = async (kind: 'manual' | 'autosave') => {
    setNotice('');
    try {
      const updated = await doctoralApi.updateTopic({ ...form, save_kind: kind });
      setForm(updated);
      setDirty(false);
    } catch (error) {
      setNotice('Не удалось сохранить тему.');
      throw error;
    }
  };

  const upload = (slot: string) => async (file: File) => {
    setNotice('');
    try {
      const updated = await doctoralApi.uploadTopicFile(slot, file);
      setForm(updated);
      setDirty(false);
    } catch {
      setNotice('Не удалось загрузить документ.');
    }
  };

  const openStored = async (relative: string | null | undefined) => {
    if (!relative) return;
    const result = await apiRequestBlob('/doctoral/process/files', {
      channel: 'platform',
      query: { relative, disposition: 'inline' },
    });
    window.open(URL.createObjectURL(result.blob), '_blank', 'noopener');
  };

  const tabs: [TopicTab, string][] = [
    ['prepare', 'Подготовка темы'],
    ['supervisor', 'Научный руководитель'],
    ['foreign', 'Зарубежный консультант'],
    ['approval', 'Утверждение и приказы'],
  ];

  return (
    <DoctoralSectionModes
      section="topic"
      onSave={save}
      autosaveSignal={dirty ? JSON.stringify(form) : undefined}
    >
    <>
      <Hero
        kicker="DISSERTATION TOPIC WORKFLOW"
        title="Тема и научные консультанты"
        text="Один последовательный процесс: подготовка темы → научный руководитель → зарубежный консультант → утверждение и приказы."
        color="#f1b93f"
      />
      {notice ? <div className="dp120-callout" style={{ marginTop: 10 }}><b>{notice}</b></div> : null}
      <div className="dp120-subtabs">
        {tabs.map(([key, label]) => (
          <button key={key} type="button" className={tab === key ? 'active gold' : ''} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'prepare' && (
        <div className="dp120-grid">
          <Card title="Тема диссертации" sub="Три языковые версии — одна связанная запись.">
            <div className="dp120-form">
              <Field label="Тема диссертации · қазақша" value={text('topic_kz')} onChange={set('topic_kz')} full />
              <Field label="Тема диссертации · русский" value={text('topic_ru')} onChange={set('topic_ru')} full />
              <Field label="Dissertation title · English" value={text('topic_en')} onChange={set('topic_en')} full />
              <Field label="Объект исследования" value={text('study_object')} onChange={set('study_object')} full />
            </div>
          </Card>
          <Card title="Документы на подготовку" sub="Свой файл докторанта, отдельно от образца.">
            <div className="dp120-list">
              <DocRow title="1 · Заявление" file={form.application_file} onPick={upload('application')} onOpen={() => openStored(form.application_file).catch(() => setNotice('Не удалось открыть файл.'))} />
              <DocRow title="2 · Обоснование темы" file={form.justification_file} onPick={upload('justification')} onOpen={() => openStored(form.justification_file).catch(() => setNotice('Не удалось открыть файл.'))} />
              <DocRow title="3 · Письмо-согласие" file={form.consent_file} onPick={upload('consent')} onOpen={() => openStored(form.consent_file).catch(() => setNotice('Не удалось открыть файл.'))} />
              <DocRow title="4 · Техническая спецификация" file={form.tech_spec_file} onPick={upload('tech_spec')} onOpen={() => openStored(form.tech_spec_file).catch(() => setNotice('Не удалось открыть файл.'))} />
              <DocRow title="5 · Сведения о научном консультанте" file={form.info_table_file} onPick={upload('consultant')} onOpen={() => openStored(form.info_table_file).catch(() => setNotice('Не удалось открыть файл.'))} />
              <DocRow title="6 · Представление" file={form.proposal_file} onPick={upload('proposal')} onOpen={() => openStored(form.proposal_file).catch(() => setNotice('Не удалось открыть файл.'))} />
            </div>
          </Card>
          <Card title="Что происходит дальше" span="span12">
            <div className="dp120-callout">
              <b>Важно:</b> вкладки — части одного процесса. Они не сбрасывают введённые данные.
            </div>
          </Card>
        </div>
      )}

      {tab === 'supervisor' && (
        <div className="dp120-grid">
          <Card title="Научный руководитель" sub="Связанная запись для НИРД, аттестации и защиты.">
            <div className="dp120-form">
              <Field label="ФИО научного руководителя" value={text('supervisor_name')} onChange={set('supervisor_name')} full />
              <Field label="Организация / кафедра / степень / звание" value={text('supervisor_info')} onChange={set('supervisor_info')} full />
            </div>
          </Card>
          <Card title="Информационная таблица" sub="Образец загружает Advisor.">
            <DocRow title="Информационная таблица научного руководителя" file={form.info_table_file} />
            <div className="dp120-course-actions">
              <button type="button" className="dp120-mini gold" onClick={onOpenAdvisorModal}>
                Образец
              </button>
            </div>
          </Card>
        </div>
      )}

      {tab === 'foreign' && (
        <div className="dp120-grid">
          <Card title="Зарубежный консультант" sub="Сведения заполняются один раз и используются во всём досье." span="span12">
            <div className="dp120-form">
              <Field label="ФИО зарубежного консультанта" value={text('foreign_consultant_name')} onChange={set('foreign_consultant_name')} full />
              <Field label="Университет / подразделение / страна / степень" value={text('foreign_consultant_info')} onChange={set('foreign_consultant_info')} full />
            </div>
            <div style={{ height: 8 }} />
            <DocRow title="Пакет зарубежного консультанта" file={form.foreign_consultant_file} />
          </Card>
        </div>
      )}

      {tab === 'approval' && (
        <div className="dp120-grid">
          <Card title="Маршрут утверждения" span="span12">
            <div className="dp120-flow">
              {[
                ['01', 'Комплект готов', '#f1b93f'],
                ['02', 'Согласование', '#42d8ff'],
                ['03', 'Приказ', '#4ce2bf'],
                ['04', 'Изменения', '#9b7cff'],
              ].map(([num, label, color]) => (
                <div key={num} className="dp120-flow-step" style={{ ['--s' as string]: color }}>
                  <b>{num}</b>
                  <strong>{label}</strong>
                </div>
              ))}
            </div>
          </Card>
          <Card title="Основной приказ" sub="Фактический документ об утверждении темы и консультантов.">
            <div className="dp120-form">
              <Field label="Номер приказа" value={text('decree_number')} onChange={set('decree_number')} />
              <Field label="Дата приказа" value={text('decree_date')} onChange={set('decree_date')} />
              <Field label="Статус" value={text('status')} onChange={set('status')} />
            </div>
          </Card>
          <Card title="Техническое задание" sub="Хранится отдельно от приказа.">
            <DocRow title="Техническое задание" file={form.tech_spec_file} />
          </Card>
        </div>
      )}
    </>
    </DoctoralSectionModes>
  );
};
